import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Plus, Trash2, MapPin, Mail, Phone, User, Pencil, ShoppingCart, FileText, Calendar, Euro, Package } from "lucide-react";

const API = '/api';

export default function Farmacias() {
  const [farmacias, setFarmacias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFarmacia, setEditingFarmacia] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    nif: "",
    morada: "",
    email: "",
    contacto: "",
    responsavel: "",
    observacoes: "",
  });

  // Histórico de encomendas
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [selectedFarmacia, setSelectedFarmacia] = useState(null);
  const [encomendas, setEncomendas] = useState([]);
  const [loadingEncomendas, setLoadingEncomendas] = useState(false);

  const fetchFarmacias = async () => {
    try {
      const response = await axios.get(`${API}/farmacias`);
      setFarmacias(response.data);
    } catch (error) {
      toast.error("Erro ao carregar farmácias");
    } finally {
      setLoading(false);
    }
  };

  const fetchEncomendasFarmacia = async (farmaciaId) => {
    setLoadingEncomendas(true);
    try {
      const response = await axios.get(`${API}/encomendas`);
      const encomendasFarmacia = response.data.filter(e => e.farmacia_id === farmaciaId);
      
      // Filtrar últimos 6 meses
      const seisMesesAtras = new Date();
      seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);
      
      const encomendasRecentes = encomendasFarmacia.filter(e => {
        const dataEncomenda = new Date(e.data_emissao);
        return dataEncomenda >= seisMesesAtras;
      });
      
      // Ordenar por data mais recente
      encomendasRecentes.sort((a, b) => new Date(b.data_emissao) - new Date(a.data_emissao));
      setEncomendas(encomendasRecentes);
    } catch (error) {
      toast.error("Erro ao carregar encomendas");
      setEncomendas([]);
    } finally {
      setLoadingEncomendas(false);
    }
  };

  const openHistorico = (farmacia) => {
    setSelectedFarmacia(farmacia);
    setHistoricoOpen(true);
    fetchEncomendasFarmacia(farmacia.id);
  };

  useEffect(() => {
    fetchFarmacias();
  }, []);

  const resetForm = () => {
    setFormData({
      nome: "",
      nif: "",
      morada: "",
      email: "",
      contacto: "",
      responsavel: "",
      observacoes: "",
    });
    setEditingFarmacia(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (farmacia) => {
    setEditingFarmacia(farmacia);
    setFormData({
      nome: farmacia.nome || "",
      nif: farmacia.nif || "",
      morada: farmacia.morada || "",
      email: farmacia.email || "",
      contacto: farmacia.contacto || "",
      responsavel: farmacia.responsavel || "",
      observacoes: farmacia.observacoes || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.nif || !formData.morada) {
      toast.error("Nome, NIF e Morada são obrigatórios");
      return;
    }

    try {
      if (editingFarmacia) {
        await axios.put(`${API}/farmacias/${editingFarmacia.id}`, formData);
        toast.success("Farmácia atualizada com sucesso");
      } else {
        await axios.post(`${API}/farmacias`, formData);
        toast.success("Farmácia adicionada com sucesso");
      }
      resetForm();
      setDialogOpen(false);
      fetchFarmacias();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar farmácia");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/farmacias/${id}`);
      toast.success("Farmácia eliminada");
      fetchFarmacias();
    } catch (error) {
      toast.error("Erro ao eliminar farmácia");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="farmacias-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">
            Farmácias
          </h1>
          <p className="text-slate-500 mt-1">
            Gestão de farmácias clientes ({farmacias.length})
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              onClick={openCreateDialog}
              data-testid="add-pharmacy-btn"
            >
              <Plus className="w-4 h-4" />
              Nova Farmácia
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingFarmacia ? "Editar Farmácia" : "Adicionar Farmácia"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="nome">Nome da Farmácia *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Farmácia Central"
                    data-testid="input-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nif">NIF *</Label>
                  <Input
                    id="nif"
                    value={formData.nif}
                    onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                    placeholder="Ex: 507571835"
                    data-testid="input-nif"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contacto">Contacto</Label>
                  <Input
                    id="contacto"
                    value={formData.contacto}
                    onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                    placeholder="Ex: 912 345 678"
                    data-testid="input-contacto"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="morada">Morada *</Label>
                  <Input
                    id="morada"
                    value={formData.morada}
                    onChange={(e) => setFormData({ ...formData, morada: e.target.value })}
                    placeholder="Ex: Rua Principal, 123, 4000-000 Porto"
                    data-testid="input-morada"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Ex: geral@farmacia.pt"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="responsavel">Nome do Responsável</Label>
                  <Input
                    id="responsavel"
                    value={formData.responsavel}
                    onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                    placeholder="Ex: Dr. João Silva"
                    data-testid="input-responsavel"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Notas adicionais sobre esta farmácia..."
                    className="min-h-[80px]"
                    data-testid="input-observacoes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-pharmacy-btn"
                >
                  {editingFarmacia ? "Guardar" : "Adicionar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pharmacies Grid */}
      {farmacias.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="font-heading font-semibold text-lg text-slate-700 mb-2">
              Nenhuma farmácia registada
            </h3>
            <p className="text-slate-500 mb-4">
              Adicione farmácias para poder criar encomendas
            </p>
            <Button
              onClick={openCreateDialog}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Farmácia
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {farmacias.map((farmacia) => (
            <Card 
              key={farmacia.id} 
              className="hover:border-blue-500/50 hover:shadow-md transition-all duration-300 group cursor-pointer"
              data-testid={`pharmacy-card-${farmacia.id}`}
              onClick={() => openHistorico(farmacia)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(farmacia);
                      }}
                      className="text-slate-400 hover:text-blue-600"
                      data-testid={`edit-pharmacy-${farmacia.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`delete-pharmacy-${farmacia.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar Farmácia?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser revertida. A farmácia será permanentemente eliminada.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(farmacia.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-1">
                  {farmacia.nome}
                </h3>
                <p className="font-mono text-sm text-slate-500 mb-3">
                  NIF: {farmacia.nif}
                </p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2 text-slate-500">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{farmacia.morada}</span>
                  </div>
                  
                  {farmacia.email && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="truncate">{farmacia.email}</span>
                    </div>
                  )}
                  
                  {farmacia.contacto && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Phone className="w-4 h-4 shrink-0" />
                      <span>{farmacia.contacto}</span>
                    </div>
                  )}
                  
                  {farmacia.responsavel && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <User className="w-4 h-4 shrink-0" />
                      <span>{farmacia.responsavel}</span>
                    </div>
                  )}
                </div>
                
                {farmacia.observacoes && (
                  <p className="mt-3 text-xs text-slate-400 bg-slate-50 p-2 rounded line-clamp-2">
                    {farmacia.observacoes}
                  </p>
                )}

                {/* Indicador de clique para ver histórico */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-center gap-2 text-sm text-blue-600 group-hover:text-blue-700">
                  <ShoppingCart className="w-4 h-4" />
                  <span>Ver histórico de encomendas</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Histórico de Encomendas */}
      <Dialog open={historicoOpen} onOpenChange={setHistoricoOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Histórico de Encomendas (últimos 6 meses) - {selectedFarmacia?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {loadingEncomendas ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : encomendas.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-semibold text-lg text-slate-700 mb-2">
                Nenhuma encomenda
              </h3>
              <p className="text-slate-500">
                Esta farmácia não tem encomendas nos últimos 6 meses.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Resumo */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{encomendas.length}</p>
                  <p className="text-xs text-slate-500">Encomendas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    €{encomendas.reduce((acc, e) => acc + (e.total_geral || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500">Total Faturado</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-600">
                    {encomendas.filter(e => e.email_enviado).length}
                  </p>
                  <p className="text-xs text-slate-500">Emails Enviados</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {encomendas.length > 0 ? new Date(encomendas[0].data_emissao).toLocaleDateString('pt-PT') : '-'}
                  </p>
                  <p className="text-xs text-slate-500">Última Encomenda</p>
                </div>
              </div>

              {/* Lista de encomendas com detalhe completo */}
              <div className="space-y-4">
                {encomendas.map((encomenda) => (
                  <div key={encomenda.id} className="border rounded-lg overflow-hidden">
                    {/* Cabeçalho da encomenda */}
                    <div className="p-4 bg-slate-50 border-b flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <span className="font-bold text-slate-900">{encomenda.numero_serie}</span>
                          <span className="ml-2 text-sm text-slate-500">
                            {new Date(encomenda.data_emissao).toLocaleDateString('pt-PT')}
                          </span>
                        </div>
                        <Badge variant={encomenda.email_enviado ? "default" : "secondary"} className="text-xs">
                          {encomenda.email_enviado ? "Email Enviado" : "Pendente"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-slate-500">
                          <Package className="w-4 h-4" />
                          {encomenda.armazenista}
                        </span>
                        <span className="font-bold text-lg text-green-600">
                          €{encomenda.total_geral?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Tabela de produtos */}
                    <div className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 text-slate-600">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium">Código</th>
                            <th className="text-left px-4 py-2 font-medium">Produto</th>
                            <th className="text-center px-4 py-2 font-medium">Qtd</th>
                            <th className="text-center px-4 py-2 font-medium">Oferta</th>
                            <th className="text-right px-4 py-2 font-medium">PVA</th>
                            <th className="text-right px-4 py-2 font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {encomenda.itens?.filter(item => item.quantidade > 0 || item.oferta > 0).map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-2 text-slate-600 font-mono text-xs">{item.codigo}</td>
                              <td className="px-4 py-2 text-slate-900">{item.descricao}</td>
                              <td className="px-4 py-2 text-center font-medium">{item.quantidade}</td>
                              <td className="px-4 py-2 text-center text-blue-600">{item.oferta > 0 ? item.oferta : '-'}</td>
                              <td className="px-4 py-2 text-right text-slate-600">€{item.pva?.toFixed(2)}</td>
                              <td className="px-4 py-2 text-right font-medium text-slate-900">€{item.total?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Observações */}
                    {encomenda.observacoes && (
                      <div className="px-4 py-2 bg-yellow-50 border-t text-sm text-yellow-800">
                        <strong>Obs:</strong> {encomenda.observacoes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
